import { useState, useContext, useEffect, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";

import SuiTypography from "components/SuiTypography";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

import { dataURLtoFile, getImageDimensions } from "utils/imageHelper";

import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Card from "@mui/material/Card";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

import FullscreenIcon from "@mui/icons-material/Fullscreen";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import ScheduleIcon from "@mui/icons-material/Schedule";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";

import { saveAs } from "file-saver";
import Prompt from "components/UpStyled/Prompt";
import moment from "moment";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import IosShareIcon from "@mui/icons-material/IosShare";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

import { Empty } from "antd";
import colors from "assets/theme/base/colors";
import { styled, alpha } from "@mui/material/styles";

import CategoryContext from "context/category-context";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Download from "yet-another-react-lightbox/plugins/download";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";

import FullCreateStyleModal from "pages/de/project/create-project/components/FullCreateStyleModal";
import { handleUploadFiles } from "utils/imageHelper";
import { nanoid } from "nanoid";

const smartdate = require("smartdate");
smartdate.init();

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 100,
    color: theme.palette.mode === "light" ? "rgb(55, 65, 81)" : theme.palette.grey[300],
    padding: 0,
    borderRadius: 2,
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: 0,
      borderRadius: 2,
    },
  },
}));

const ResultView = ({
  resultData,
  setResultData,
  sketchData,
  handleSetWorkingImage,
  setIsDirty,
  extra,
  handleToggleFavorite,
}) => {
  const [imgsViewerVisible, setImgsViewerVisible] = useState(false);
  const [imgsViewerCurrent, setImgsViewerCurrent] = useState(0);
  const [deleteVisible, setDeleteVisible] = useState(false); //delete the image in dataset
  const [deleteResultVisible, setDeleteResultVisible] = useState(false); //delete dataset
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [createStyleVisible, setCreateStyleVisible] = useState(false);
  const [styleInitialData, setStyleInitialData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [copiedText, setCopiedText] = useState();

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEx, setAnchorEx] = useState(null);

  const {
    tree,
    actions: { findNodeByID, findParents },
  } = useContext(CategoryContext);

  const open = Boolean(anchorEl);
  const handleClickResult = (event, result) => {
    setAnchorEl(event.currentTarget);

    setSelectedResult(result);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  let images = [];

  for (let i = 0; i < resultData.length; i++) {
    let rd_images = resultData[i]?.images || [];
    for (let j = 0; j < rd_images.length; j++) {
      images.push(rd_images[j]);
    }
  }

  const openImgsViewer = () => setImgsViewerVisible(true);
  const closeImgsViewer = () => setImgsViewerVisible(false);

  const handleFullScreen = (e, url) => {
    const index = images.findIndex((x) => x.url == url || x.imgurl == url);
    if (index > -1) {
      setImgsViewerCurrent(index);
      openImgsViewer();
    }
  };

  const handleDownload = (e, url) => {
    saveAs(url);
  };

  const handleClickFavorite = async (url, findIndex) => {
    const { width, height } = await getImageDimensions(url);
    const data = {
      ...sketchData,
      width,
      height,
      imgurl: url,
    };
    handleToggleFavorite(data, findIndex);
  };

  const handleDeleteResult = (rd) => {
    const newresultData = resultData.filter((r) => rd.taskData.id !== r.taskData.id);
    setResultData(newresultData);
    setIsDirty(true);
  };

  const handleDeleteResultImage = (e, aiResult, index) => {
    const newresultData = [];

    for (let i = 0; i < resultData.length; i++) {
      if (aiResult?.taskData?.id === resultData[i]?.taskData?.id) {
        const newAiResult = {
          ...resultData[i],
          images: resultData[i].images.filter((x, inx) => inx !== index),
        };

        //delete the whole result if it doesn't contain any images
        if (newAiResult.images.length > 0) {
          newresultData.push(newAiResult);
        }
      } else {
        newresultData.push(resultData[i]);
      }
    }

    setResultData(newresultData);
    setIsDirty(true);
  };

  const handleImportResult = (result) => {
    const conditional = result
      ? result?.taskData?.task_meta?.conditional
      : selectedResult?.taskData?.task_meta?.conditional;
    const task_meta = result ? result?.taskData?.task_meta : selectedResult?.taskData?.task_meta;
    let sketch_img = "";
    let fabric_img = "";
    let mode = "image";
    if (typeof conditional == "string") {
      sketch_img = conditional;
    } else if (typeof conditional == "object") {
      sketch_img = conditional[0];
      fabric_img = conditional[1];
      mode = "fabric";
    }
    handleSetWorkingImage(sketch_img, fabric_img, mode, task_meta);

    handleClose();
  };

  const handleImportResultToPost = async () => {
    try {
      setUploading(true);

      const url = selectedResult?.url || selectedResult?.imgurl;
      if (url) {
        const myfile = await fetch(url)
          .then((response) => response.blob())
          .then((blob) => new File([blob], nanoid(), { type: blob.type }));

        const imageFiles = (await handleUploadFiles([myfile])) || [];
        if (imageFiles && imageFiles.length > 0) {
          window.open(`/post/create?purl=${imageFiles[0]?.url}&ptype=image`, "_blank");
        }
      }
      setAnchorEx(null);
      setUploading(false);
    } catch (err) {
      setUploading(false);

      console.log(err);
    }
  };

  const handleImportResultToStyle = async () => {
    try {
      const style_number = "TEMP_" + moment().format("YYYYMMHHmmss");
      setUploading(true);
      const url = selectedResult?.url || selectedResult?.imgurl;

      const myfile = await fetch(url)
        .then((response) => response.blob())
        .then((blob) => new File([blob], nanoid(), { type: blob.type }));

      const imageFiles = (await handleUploadFiles([myfile])) || [];
      let payload = {
        style_name: "Untitled",
        style_number: style_number,
        description: "",
        collection_id: null,
        year: moment(),
        imageFiles,
      };
      setStyleInitialData(payload);
      setCreateStyleVisible(true);
      setAnchorEx(null);
      setUploading(false);
    } catch (err) {
      console.log(err);
      setUploading(false);
    }
  };

  const handleAfterOk = (style) => {
    window.open(`/workspace/styles-master?id=${style?.data?.product?.id}`, "_blank");
    setCreateStyleVisible(false);
  };

  const favorite_list = extra?.favorite_list || [];

  return (
    <Card sx={{ maxHeight: 600, overflow: "auto", borderRadius: 2 }}>
      <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
        <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
        <SuiTypography variant="h6" fontWeight="medium" color="info">
          Processing...
        </SuiTypography>
      </Backdrop>
      <Lightbox
        open={imgsViewerVisible}
        close={closeImgsViewer}
        slides={images.map((x, index) => ({ src: x?.url || x?.imgurl, id: index }))}
        plugins={[Zoom, Download, Thumbnails]}
        zoom={{ maxZoomPixelRatio: 3 }}
        index={imgsViewerCurrent}
      />

      <SuiBox mb={1}>
        {resultData.map((result) => {
          const conditional = result?.taskData?.task_meta?.conditional;
          let sketch_img = "";
          let fabric_img = "";
          if (typeof conditional == "string") {
            sketch_img = conditional;
          } else if (typeof conditional == "object") {
            sketch_img = conditional[0];
            fabric_img = conditional[1];
          }

          let prompt_text = result.taskData.task_meta.prompt || "";
          let prompt_array_text =
            result.taskData.task_meta.prompt_array &&
            result.taskData.task_meta.prompt_array.length > 0
              ? result.taskData.task_meta.prompt_array.join(",")
              : "";

          return (
            <div>
              <SuiBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  mb: 0.5,
                  height: 74,
                  backgroundColor: colors.light.main,
                }}
              >
                <SuiBox sx={{ display: "flex", gap: 1, flexGrow: 1 }}>
                  <SuiBox
                    sx={{
                      display: { xs: "none", sm: "none", md: "flex", lg: "flex" },
                      alignItems: "center",
                      gap: 1,
                      minWidth: 120,
                    }}
                  >
                    <ScheduleIcon fontSize="small" />
                    {smartdate.format(new Date(result?.dt))}
                  </SuiBox>
                  <SuiBox
                    sx={{
                      display: { xs: "flex", sm: "flex", md: "none", lg: "none" },
                      alignItems: "center",
                      gap: 1,
                      minWidth: 20,
                    }}
                  >
                    <Tooltip title={smartdate.format(new Date(result?.dt))}>
                      <ScheduleIcon fontSize="small" />
                    </Tooltip>
                  </SuiBox>

                  {sketch_img ? (
                    <SuiBox
                      display="flex"
                      gap={0.5}
                      alignItems="center"
                      sx={{ minWidth: 120, overflow: "hidden" }}
                    >
                      <SuiTypography variant="body1" fontWeight="bold" color="dark">
                        Image:
                      </SuiTypography>
                      <div>
                        <ImageCardWrapper
                          data={{ url: sketch_img, type: "image/*" }}
                          alt={"image_conditional"}
                          width={65}
                          height={65}
                          sx={{ cursor: "pointer" }}
                          onClick={(e) => handleClickResult(e, result)}
                        />
                      </div>
                    </SuiBox>
                  ) : null}
                  {fabric_img ? (
                    <SuiBox display="flex" gap={0.5} alignItems="center" sx={{ minWidth: 65 }}>
                      <div>
                        <ImageCardWrapper
                          data={{ url: fabric_img, type: "image/*" }}
                          alt={"image_fabric"}
                          width={65}
                          height={65}
                          sx={{ cursor: "pointer", height: "100%", width: "100%" }}
                        />
                      </div>
                    </SuiBox>
                  ) : null}
                  <SuiBox display="flex" flexDirection="column" justifyContent="center">
                    {prompt_text ? (
                      <SuiBox display="flex" gap={0.5} alignItems="center">
                        <SuiBox
                          sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 85 }}
                        >
                          <SuiTypography variant="body1" fontWeight="bold" color="dark">
                            Prompts:{" "}
                          </SuiTypography>
                          <div>
                            <a
                              href=""
                              onClick={(e) => {
                                e.preventDefault();
                                navigator.clipboard.writeText(prompt_text);
                                setCopiedText(prompt_text);
                              }}
                            >
                              {copiedText == prompt_text ? (
                                <CheckIcon color="success" fontSize="small" />
                              ) : (
                                <ContentCopyIcon fontSize="small" />
                              )}
                            </a>
                          </div>
                        </SuiBox>
                        <div>
                          <SuiBox sx={{ px: 1 }}>
                            <Tooltip title={prompt_text}>
                              <SuiTypography
                                variant="body1"
                                color="text"
                                sx={{
                                  fontSize: { xs: 12, sm: 12, md: 12, lg: 14 },
                                  lineHeight: 1.2,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: "2",
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {prompt_text}
                              </SuiTypography>
                            </Tooltip>
                          </SuiBox>
                        </div>
                      </SuiBox>
                    ) : null}
                    {prompt_array_text ? (
                      <SuiBox display="flex" gap={0.5} alignItems="center">
                        <SuiBox
                          sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 65 }}
                        >
                          <SuiTypography variant="body1" fontWeight="bold" color="dark">
                            Tags:{" "}
                          </SuiTypography>
                          <div>
                            <a
                              href=""
                              onClick={(e) => {
                                e.preventDefault();
                                navigator.clipboard.writeText(prompt_array_text);
                                setCopiedText(prompt_array_text);
                              }}
                            >
                              {copiedText == prompt_array_text ? (
                                <CheckIcon color="success" fontSize="small" />
                              ) : (
                                <ContentCopyIcon fontSize="small" />
                              )}
                            </a>
                          </div>
                        </SuiBox>
                        <div>
                          <SuiBox sx={{ px: 1 }}>
                            <Tooltip title={prompt_array_text}>
                              <SuiTypography
                                variant="body1"
                                color="text"
                                sx={{
                                  fontSize: { xs: 12, sm: 12, md: 12, lg: 14 },
                                  lineHeight: 1.2,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: "1",
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {prompt_array_text}
                              </SuiTypography>
                            </Tooltip>
                          </SuiBox>
                        </div>
                      </SuiBox>
                    ) : null}
                  </SuiBox>
                </SuiBox>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  gap={0.5}
                  sx={{ minWidth: 100 }}
                >
                  <Tooltip title="Keep working on this result.">
                    <SuiButton
                      color="dark"
                      variant="gradient"
                      size="small"
                      onClick={() => {
                        handleImportResult(result);
                      }}
                      sx={{
                        padding: "0px 12px",
                        fontSize: 12,
                        lineHeight: 0,
                        minHeight: 30,

                        textTransform: "capitalize",
                        borderRadius: "4px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Keep Working
                    </SuiButton>
                  </Tooltip>
                  <Tooltip title="Delete this result.">
                    <SuiButton
                      color="error"
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setSelectedResult(result);
                        setDeleteResultVisible(true);
                      }}
                      sx={{
                        padding: "0px 8px",
                        fontSize: 12,
                        lineHeight: 0,
                        minHeight: 30,
                        textTransform: "capitalize",
                        borderRadius: "4px",
                      }}
                    >
                      Delete
                    </SuiButton>
                  </Tooltip>
                </SuiBox>
              </SuiBox>

              <ResponsiveMasonry
                columnsCountBreakPoints={{ 250: 1, 650: 2, 900: 3, 1300: 4, 1600: 5 }}
              >
                <Masonry>
                  {result.images.map((x, index) => {
                    const url = x?.url || x?.imgurl;
                    const findIndex = favorite_list.findIndex(
                      (item) => item?.imgurl == url || item?.url == url
                    );

                    return (
                      <SuiBox sx={{ mb: 1, mx: 1 }}>
                        <div
                          className="hover-parent"
                          style={{
                            position: "relative",
                            minHeight: 200,
                            borderRadius: 12,
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={x?.url || x?.imgurl}
                            alt={"image_" + index}
                            style={{ width: "100%", height: "auto", cursor: "pointer" }}
                            onClick={(e) => handleFullScreen(e, url)}
                          />

                          <SuiBox
                            className="hover-children"
                            sx={{ position: "absolute", top: 4, right: 4, left: 4, width: "auto" }}
                          >
                            <SuiBox
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              gap={1}
                            >
                              <IconButton
                                size="small"
                                color="dark"
                                sx={{
                                  backgroundColor: "#ffffff",
                                  "&:hover": {
                                    backgroundColor: "#e3e3e3",
                                  },
                                }}
                                onClick={(e) => {
                                  setAnchorEx(e.currentTarget);
                                  setSelectedResult(x);
                                }}
                              >
                                <IosShareIcon />
                              </IconButton>

                              <SuiBox
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                                gap={1}
                              >
                                <Tooltip title={"Delete"}>
                                  <IconButton
                                    size="small"
                                    color="dark"
                                    sx={{
                                      backgroundColor: "#ffffff",
                                      "&:hover": {
                                        backgroundColor: "#e3e3e3",
                                      },
                                    }}
                                    onClick={(e) => {
                                      setDeleteVisible(true);
                                      setDeleteIndex(index);
                                      setSelectedResult(result);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title={"Fullscreen"}>
                                  <IconButton
                                    size="small"
                                    color="dark"
                                    sx={{
                                      backgroundColor: "#ffffff",
                                      "&:hover": {
                                        backgroundColor: "#e3e3e3",
                                      },
                                    }}
                                    onClick={(e) => handleFullScreen(e, url)}
                                  >
                                    <FullscreenIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={"Download"}>
                                  <IconButton
                                    size="small"
                                    color="dark"
                                    sx={{
                                      backgroundColor: "#ffffff",
                                      "&:hover": {
                                        backgroundColor: "#e3e3e3",
                                      },
                                    }}
                                    onClick={(e) => handleDownload(e, url)}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                                {findIndex == -1 ? (
                                  <Tooltip title={"Add Favorite"}>
                                    <IconButton
                                      size="small"
                                      color="dark"
                                      sx={{
                                        backgroundColor: "#ffffff",
                                        "&:hover": {
                                          backgroundColor: "#e3e3e3",
                                        },
                                      }}
                                      onClick={(e) => handleClickFavorite(url, findIndex)}
                                    >
                                      <FavoriteBorderRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={"Remove Favorite"}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      sx={{
                                        backgroundColor: "#ffffff",
                                        "&:hover": {
                                          backgroundColor: "#e3e3e3",
                                        },
                                      }}
                                      onClick={(e) => handleClickFavorite(url, findIndex)}
                                    >
                                      <FavoriteRoundedIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </SuiBox>
                            </SuiBox>
                          </SuiBox>

                          <div
                            className="overlay"
                            style={{
                              textAlign: "center",
                              width: "100%",
                              zIndex: 2,
                            }}
                          >
                            <SuiButton
                              variant="contained"
                              color="white"
                              size="small"
                              sx={{
                                whiteSpace: "nowrap",
                                textTransform: "capitalize",
                                width: "100%",
                                mb: 1,
                              }}
                              onClick={() =>
                                handleSetWorkingImage(
                                  url,
                                  null,
                                  "image",
                                  result?.taskData?.task_meta
                                )
                              }
                            >
                              Set as Working Image
                            </SuiButton>
                          </div>
                        </div>
                      </SuiBox>
                    );
                  })}
                </Masonry>
              </ResponsiveMasonry>
            </div>
          );
        })}
        {resultData.length == 0 ? <Empty /> : null}
      </SuiBox>

      {deleteResultVisible ? (
        <Prompt
          heading="Delete Confirmation"
          text={`Are you certain you want to delete this result?`}
          visible={deleteResultVisible}
          okText="Delete"
          okColor="error"
          handleOk={() => {
            handleDeleteResult(selectedResult);
            setDeleteResultVisible(false);
          }}
          handleClose={() => setDeleteResultVisible(false)}
        />
      ) : null}
      {deleteVisible ? (
        <Prompt
          heading="Delete Confirmation"
          text={`Are you certain you want to delete this result image?`}
          visible={deleteVisible}
          okText="Delete"
          okColor="error"
          handleOk={(e) => {
            handleDeleteResultImage(e, selectedResult, deleteIndex);
            setDeleteVisible(false);
          }}
          handleClose={() => setDeleteVisible(false)}
        />
      ) : null}

      <StyledMenu
        id="lock-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{ padding: 0 }}
      >
        <MenuItem onClick={(event) => handleImportResult(event)}>Set as Working Image</MenuItem>
      </StyledMenu>

      <StyledMenu
        id="export-menu"
        anchorEl={anchorEx}
        open={Boolean(anchorEx)}
        onClose={() => setAnchorEx(null)}
        sx={{ padding: 0 }}
      >
        <MenuItem onClick={(event) => handleImportResultToPost(event)}>
          Create post using the image
        </MenuItem>
        <MenuItem onClick={(event) => handleImportResultToStyle(event)}>
          Create style using the image
        </MenuItem>
      </StyledMenu>

      {createStyleVisible ? (
        <FullCreateStyleModal
          handleClose={() => setCreateStyleVisible(false)}
          visible={createStyleVisible}
          isEdit={false}
          categoryTree={tree}
          handleAfterOk={handleAfterOk}
          initialData={styleInitialData}
        />
      ) : null}
    </Card>
  );
};

export default ResultView;
